"use client"; // for Next.js App Router
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { SelectLoggedUser } from "@/redux/Auth";
import { X, MessageCircle, Bell, Eye, Zap } from "lucide-react";

const TelegramLogin = () => {
  const loggedUser = useAppSelector(SelectLoggedUser);
  const authToken = loggedUser?.userToken || "";
  const telegram_subscription =
    loggedUser?.userData?.telegram_subscribed || false;
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Don't run if no token or already subscribed
    if (!authToken || telegram_subscription) return;

    // Show popup automatically after a short delay
    setShowPopup(true);
  }, [authToken, telegram_subscription]);

  useEffect(() => {
    // Only load Telegram script when popup is shown and user is not subscribed
    if (!showPopup || !authToken || telegram_subscription) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", "eduvalu_bot");
    script.setAttribute("data-size", "large");
    script.setAttribute(
      "data-auth-url",
      `https://backend.eduvalu.com/api/v1/telegram/auth?token=${authToken}`
    );
    script.setAttribute("data-request-access", "write");

    const container = document.getElementById("telegram-button-container");
    if (container) {
      container.innerHTML = "";
      container.appendChild(script);
    }
  }, [showPopup, authToken, telegram_subscription]);

  // Don't render anything if already subscribed
  if (telegram_subscription) return null;

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-300">
            {/* Close Button */}
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              dir="ltr"
            >
              <X size={20} />
            </button>

            <div className="p-8 text-center" dir="rtl">
              {/* Header */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                متابعة أولية
              </h2>

              <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                اربط حسابك مع تليجرام واحصل على تجربة تعلم أفضل
              </p>

              {/* Features */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 text-right">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mt-0.5">
                    <Bell className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">
                      رسائل تشجيعية
                    </h3>
                    <p className="text-gray-600 text-xs">
                      رسائل علشان نشجعك تذاكر
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 text-right">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mt-0.5">
                    <Eye className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">
                      متابعة مستمرة
                    </h3>
                    <p className="text-gray-600 text-xs">هنتابعك أول بأول</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 text-right">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mt-0.5">
                    <Zap className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">
                      ربط سهل
                    </h3>
                    <p className="text-gray-600 text-xs">سهل تربطه</p>
                  </div>
                </div>
              </div>

              {/* Telegram Button Container */}
              <div id="telegram-button-container" className="mb-6" />

              {/* Skip Option */}
              <button
                onClick={() => setShowPopup(false)}
                className="text-gray-500 text-sm hover:text-gray-700 transition-colors"
              >
                تخطي الآن
              </button>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-2xl -z-10"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-50 to-transparent rounded-2xl -z-10"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default TelegramLogin;
